import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect, useState, lazy, Suspense } from 'react'
import { supabase } from './lib/supabase'
import { auditLog } from './lib/audit'

// ── Modo mantenimiento ─────────────────────────────────────────
// Cambiar a false para publicar la plataforma al público
// Bypass de preview: agrega ?v=qapaq2026 a cualquier URL para acceder
const MANTENIMIENTO = false
const PREVIEW_KEY = 'qapaq2026'

// Rutas que el modo mantenimiento NO puede tapar (BACKLOG 0.20).
//
// El enlace de recuperación de contraseña llega por correo y no trae la clave
// de preview, así que sin esta excepción aterrizaría en la pantalla de
// mantenimiento y el token moriría en la puerta — que es exactamente lo que
// pasaba. La alternativa era incluir `?v=…` en el correo, que filtraría la
// clave del bypass a cualquiera que reciba uno.
const RUTAS_SIEMPRE_ABIERTAS = ['/app/clave']

// Rutas que no dependen de la sesión. Se muestran sin esperar a Supabase: si
// la base está pausada o lenta, la portada no puede quedar tapada por el
// spinner — es lo que ven los visitantes y lo que indexa Google (ya viene
// prerenderizada en el HTML; esperar aquí la borraba y dejaba sólo el spinner).
const RUTAS_PUBLICAS = ['/', '/transportistas', '/empresas', '/como-funciona', '/quienes-somos', '/app/terminos', '/app/privacidad']

// Si Supabase no responde en este plazo, se deja de esperar y se avisa en vez
// de girar para siempre.
const LIMITE_SESION_MS = 8000

import LandingPage from './pages/LandingPage'
import PaginaInfo from './pages/PaginaInfo'

import Terminos from './pages/Terminos'
import Privacidad from './pages/Privacidad'

import { ROL } from './lib/roles'

import './styles/global.css'

const Layout = lazy(() => import('./components/layout/Layout'))
const Login = lazy(() => import('./pages/Login'))
const Registro = lazy(() => import('./pages/Registro'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const PublicarCarga = lazy(() => import('./pages/PublicarCarga'))
const BuscarCargas = lazy(() => import('./pages/BuscarCargas'))
const MisEnvios = lazy(() => import('./pages/MisEnvios'))
const DetalleEnvio = lazy(() => import('./pages/DetalleEnvio'))
const Perfil = lazy(() => import('./pages/Perfil'))
const AdminFlota = lazy(() => import('./pages/AdminFlota'))
const Chat = lazy(() => import('./pages/Chat'))
const Repositorio = lazy(() => import('./pages/Repositorio'))
const CRM = lazy(() => import('./pages/CRM'))
const RemateView = lazy(() => import('./pages/RemateView'))
const PayPerUse = lazy(() => import('./pages/PayPerUse'))
const WhatsAppConfig = lazy(() => import('./pages/WhatsAppConfig'))
const AdminFinanzas = lazy(() => import('./pages/AdminFinanzas'))
const ControlGPS = lazy(() => import('./pages/ControlGPS'))
const ReporteMercado = lazy(() => import('./pages/ReporteMercado'))
const Prospectos = lazy(() => import('./pages/Prospectos'))
const PagoResultado = lazy(() => import('./pages/PagoResultado'))
const EntregaQR = lazy(() => import('./pages/EntregaQR'))
const Sugerencias = lazy(() => import('./pages/Sugerencias'))
const MisDatos = lazy(() => import('./pages/MisDatos'))
const AdminPrivacidad = lazy(() => import('./pages/AdminPrivacidad'))
const Desistir = lazy(() => import('./pages/Desistir'))
const VerificarAcuerdo = lazy(() => import('./pages/VerificarAcuerdo'))
const RestablecerClave = lazy(() => import('./pages/RestablecerClave'))

const BENEFICIOS = {
  transportista: [
    { icon: '🔍', txt: 'Encuentra carga en tu ruta de vuelta antes de salir' },
    { icon: '⚡', txt: 'Sin llamadas — filtra, oferta y adjudícate en minutos' },
    { icon: '💳', txt: 'Cobras directo al oferente: QAPAQ no se queda con tu pago' },
    { icon: '📍', txt: 'GPS incluido — sin tracker adicional' },
  ],
  empresa: [
    { icon: '💰', txt: 'Sin comisiones ocultas — el precio lo fija el mercado' },
    { icon: '⚡', txt: 'Cotizaciones en tiempo real, no en días' },
    { icon: '📦', txt: 'Ve el Score de Confiabilidad del transportista antes de elegir' },
    { icon: '🔒', txt: 'Pagas directo al transportista; QAPAQ solo cobra su comisión' },
  ],
}

function PaginaMantenimiento() {
  const [rol, setRol]       = useState('transportista')
  const [email, setEmail]   = useState('')
  const [nombre, setNombre] = useState('')
  const [estado, setEstado] = useState('idle') // idle | cargando | ok | error | duplicado

  async function handleWaitlist() {
    if (!email) return
    setEstado('cargando')

    const { error } = await supabase.from('waitlist').insert({ email, rol, nombre: nombre || null })

    if (error) {
      if (error.code === '23505') { setEstado('duplicado'); return }
      setEstado('error'); return
    }

    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', { content_name: rol, content_category: 'waitlist' })
    }

    supabase.functions.invoke('waitlist-welcome', {
      body: { email, rol, nombre: nombre || null },
    }).catch(err => console.warn('waitlist-welcome:', err))

    setEstado('ok')
  }

  const F = `'Montserrat', 'Helvetica Neue', Arial, sans-serif`

  const S = {
    page: {
      minHeight: '100vh', background: '#111111', fontFamily: F,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    },
    hero: { width: '100%', maxWidth: 640, padding: '3rem 1.5rem 2rem', textAlign: 'center' },
    badge: {
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'rgba(255,90,0,0.15)', border: '1px solid rgba(255,90,0,0.35)',
      borderRadius: 99, padding: '0.3rem 1rem', color: '#FF5A00',
      fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.1em',
      textTransform: 'uppercase', marginBottom: '1.25rem',
    },
    dot: { width: 7, height: 7, background: '#FF5A00', borderRadius: '50%', animation: 'qpulse 1.2s infinite' },
    h1: { color: '#fff', fontSize: 'clamp(1.9rem, 5.5vw, 2.8rem)', fontWeight: 900, margin: '0 0 0.9rem', lineHeight: 1.1 },
    sub: { color: 'rgba(255,255,255,0.55)', fontSize: '1rem', lineHeight: 1.75, margin: '0 0 2rem' },
    rolWrap: { display: 'flex', gap: 10, marginBottom: '1.75rem', justifyContent: 'center' },
    rolBtn: (active) => ({
      flex: 1, maxWidth: 220,
      background: active ? 'rgba(255,90,0,0.15)' : 'rgba(255,255,255,0.04)',
      border: active ? '1.5px solid rgba(255,90,0,0.5)' : '1.5px solid rgba(255,255,255,0.1)',
      borderRadius: 12, padding: '0.85rem 0.5rem',
      color: active ? '#fff' : 'rgba(255,255,255,0.4)',
      fontSize: '0.82rem', fontWeight: 800, cursor: 'pointer', fontFamily: F,
    }),
    rolEmoji: { fontSize: '1.3rem', display: 'block', marginBottom: 4 },
    bList: {
      width: '100%', maxWidth: 560,
      display: 'flex', flexDirection: 'column', gap: 8,
      marginBottom: '1.75rem', padding: '0 1.5rem', boxSizing: 'border-box',
    },
    bItem: {
      display: 'flex', alignItems: 'flex-start', gap: 12,
      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 10, padding: '0.75rem 1rem',
    },
    bIcon: { fontSize: '1.15rem', lineHeight: 1.4, flexShrink: 0 },
    bTxt: { color: 'rgba(255,255,255,0.75)', fontSize: '0.88rem', fontWeight: 600, lineHeight: 1.5 },
    formWrap: { width: '100%', maxWidth: 480, padding: '0 1.5rem 1rem', boxSizing: 'border-box' },
    form: {
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 16, padding: '1.5rem',
    },
    formTitle: { color: '#fff', fontSize: '0.95rem', fontWeight: 800, marginBottom: '1.1rem', textAlign: 'center' },
    label: {
      display: 'block', color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem',
      fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.35rem',
    },
    input: {
      width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.13)',
      borderRadius: 8, padding: '0.72rem 1rem', color: '#fff', fontSize: '0.95rem',
      marginBottom: '0.8rem', outline: 'none', boxSizing: 'border-box', fontFamily: F,
    },
    btn: {
      width: '100%', background: '#FF5A00', border: 'none', borderRadius: 10,
      padding: '0.9rem', color: '#fff', fontSize: '1rem', fontWeight: 800,
      cursor: 'pointer', letterSpacing: '0.03em', fontFamily: F,
    },
    proofBar: { display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center', padding: '1.5rem 1.5rem 0', marginBottom: '1.25rem' },
    proofItem: { textAlign: 'center' },
    proofNum: { color: '#FF5A00', fontSize: '1.35rem', fontWeight: 900, lineHeight: 1 },
    proofLbl: { color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem', fontWeight: 600, marginTop: 3 },
    footer: { color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem', padding: '1.5rem', textAlign: 'center', lineHeight: 1.8 },
    footerLink: { color: 'rgba(255,255,255,0.2)', textDecoration: 'none' },
  }

  const beneficios = BENEFICIOS[rol]
  const btnLabel = rol === 'transportista' ? 'Quiero encontrar más carga →' : 'Quiero pagar menos en fletes →'

  return (
    <div style={S.page}>
      <style>{`@keyframes qpulse{0%,100%{opacity:1}50%{opacity:0.25}}@keyframes qfadein{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}.qb{animation:qfadein 0.3s ease both}`}</style>

      <div style={S.hero}>
        <img src="/qapaq-isotipo-negro.png" alt="QAPAQ" style={{ height: 72, objectFit: 'contain', display: 'block', margin: '0 auto 1.5rem' }} />
        <div style={S.badge}>
          <span style={S.dot} />
          Próximamente en Chile
        </div>
        <h1 style={S.h1}>
          El flete en Chile<br />
          <span style={{ color: '#FF5A00' }}>sin intermediarios.</span>
        </h1>
        <p style={S.sub}>
          QAPAQ conecta empresas y transportistas directamente.<br />
          Remates en tiempo real. Precio de mercado. GPS incluido.
        </p>

        <div style={S.rolWrap}>
          <button style={S.rolBtn(rol === 'transportista')} onClick={() => setRol('transportista')}>
            <span style={S.rolEmoji}>🚛</span>
            Soy transportista
          </button>
          <button style={S.rolBtn(rol === 'empresa')} onClick={() => setRol('empresa')}>
            <span style={S.rolEmoji}>📦</span>
            Soy empresa
          </button>
        </div>
      </div>

      <div style={S.bList}>
        {beneficios.map((b, i) => (
          <div key={i} className="qb" style={{ ...S.bItem, animationDelay: `${i * 0.06}s` }}>
            <span style={S.bIcon}>{b.icon}</span>
            <span style={S.bTxt}>{b.txt}</span>
          </div>
        ))}
      </div>

      <div style={S.formWrap}>
        {estado === 'ok' ? (
          <div style={{ ...S.form, textAlign: 'center', padding: '2.25rem 1.5rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🦙</div>
            <p style={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem', margin: '0 0 0.5rem' }}>
              ¡Estás en la lista!
            </p>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem', margin: 0, lineHeight: 1.6 }}>
              Te avisamos por email cuando QAPAQ abra sus puertas.<br />
              Revisa tu bandeja — ya te enviamos un mensaje de bienvenida.
            </p>
          </div>
        ) : (
          <div style={S.form}>
            <p style={S.formTitle}>
              {rol === 'transportista' ? '¿Quieres encontrar más carga?' : '¿Quieres pagar el precio real del flete?'}
            </p>

            <label style={S.label}>Nombre (opcional)</label>
            <input style={S.input} type="text" placeholder="Juan Pérez" value={nombre} onChange={e => setNombre(e.target.value)} />

            <label style={S.label}>Email *</label>
            <input style={S.input} type="email" placeholder="tu@email.com" value={email} onChange={e => setEmail(e.target.value)} />

            {estado === 'duplicado' && (
              <p style={{ color: '#FF5A00', fontSize: '0.85rem', margin: '-0.3rem 0 0.75rem', fontWeight: 700 }}>
                ✓ Ese email ya está registrado — te avisamos al lanzar.
              </p>
            )}
            {estado === 'error' && (
              <p style={{ color: '#ff4444', fontSize: '0.85rem', margin: '-0.3rem 0 0.75rem' }}>
                Error al registrar. Intenta de nuevo.
              </p>
            )}

            <button style={S.btn} disabled={estado === 'cargando'} onClick={handleWaitlist}>
              {estado === 'cargando' ? 'Guardando...' : btnLabel}
            </button>
          </div>
        )}
      </div>

      <div style={S.proofBar}>
        {[
          { num: '+15', lbl: 'regiones cubiertas' },
          { num: '24/7', lbl: 'disponibilidad' },
          { num: 'GPS', lbl: 'tracking incluido' },
          { num: '0%', lbl: 'comisiones ocultas' },
        ].map(p => (
          <div key={p.lbl} style={S.proofItem}>
            <div style={S.proofNum}>{p.num}</div>
            <div style={S.proofLbl}>{p.lbl}</div>
          </div>
        ))}
      </div>

      <p style={S.footer}>
        contacto@qapaq.cl · Sin spam, solo el anuncio de lanzamiento.<br />
        <a href="/app/terminos" style={S.footerLink}>Términos y Condiciones</a>
        {' · '}
        <a href="/app/privacidad" style={S.footerLink}>Privacidad</a>
      </p>
    </div>
  )
}

function RutaProtegida({ children, session, usuario }) {
  if (!session) return <Navigate to="/app/login" replace />

  // Perfil aún cargando
  if (usuario === undefined) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <div className="spinner" />
    </div>
  )

  // ── Compuerta por estado de la cuenta ───────────────────────────────────
  //
  // 🔴 Acá había un bug que dejaba la aplicación entera inalcanzable. La
  // condición era `usuario.estado !== 'activo'`, y **`activo` no existe**: el
  // CHECK de `usuario.estado` admite `pendiente`, `verificado`, `observado`,
  // `suspendido`, `expulsado` y `eliminado`. Ningún usuario podía cumplirla,
  // así que los 13 de producción —8 de ellos verificados— quedaban atrapados
  // en «Tu cuenta está en revisión», con la pantalla mostrando debajo «Estado
  // de tu cuenta: verificado» y contradiciéndose sola.
  //
  // Lo mismo con `'bloqueado'`, que tampoco existe: el valor real es
  // `suspendido`.
  //
  // Es el patrón del 8.16 —código escrito contra un esquema que ya no es el
  // que corre— en su versión más cara. Antes de tocar un literal de `estado`,
  // `tipo` o `rol`, buscarlo en la tabla de CHECK de `docs/esquema-real.md`.
  if (!usuario) return <CuentaRestringida estado="pendiente" />

  // `observado` SÍ entra, y es deliberado: la decisión 2.5 dice que la
  // denuncia genera observación y sólo la condena expulsa. Si una observación
  // cerrara el acceso, cualquier denuncia bastaría para dejar a alguien fuera.
  const PUEDE_ENTRAR = ['verificado', 'observado']
  if (!PUEDE_ENTRAR.includes(usuario.estado)) {
    return <CuentaRestringida estado={usuario.estado} />
  }

  return children
}

function CuentaRestringida({ estado }) {
  const info = {
    pendiente: {
      icono: '⏳',
      titulo: 'Tu cuenta está en revisión',
      texto: 'Recibimos tu registro correctamente. Nuestro equipo verificará tus datos y documentos antes de activar tu acceso. Te avisaremos por correo una vez que tu cuenta esté lista.',
      color: '#FF5A00',
      bg: '#FFF3E5',
    },
    // El valor real del CHECK es `suspendido`, no `bloqueado`.
    suspendido: {
      icono: '🚫',
      titulo: 'Cuenta suspendida',
      texto: 'Tu cuenta ha sido suspendida temporalmente. Si crees que es un error, comunícate con nosotros para resolverlo.',
      color: '#c53030',
      bg: '#fff5f5',
    },
    expulsado: {
      icono: '🚫',
      titulo: 'Cuenta cerrada',
      texto: 'Tu cuenta fue cerrada de forma definitiva. Si crees que es un error, escríbenos y lo revisamos.',
      color: '#c53030',
      bg: '#fff5f5',
    },
    eliminado: {
      icono: '🗑️',
      titulo: 'Cuenta eliminada',
      texto: 'Esta cuenta fue eliminada a solicitud del titular. Si necesitas volver a operar, tendrás que registrarte de nuevo.',
      color: '#6B7280',
      bg: '#F3F4F6',
    },
  }
  const { icono, titulo, texto, color, bg } = info[estado] ?? info.pendiente

  async function cerrarSesion() {
    await supabase.auth.signOut()
    window.location.href = '/'
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9F9F9', padding: '1.5rem', fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ maxWidth: 460, width: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '2.5rem 2rem', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: '1rem' }}>{icono}</div>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#111', margin: '0 0 0.75rem' }}>{titulo}</h2>
        <p style={{ fontSize: '0.9rem', color: '#555', lineHeight: 1.7, marginBottom: '1.5rem' }}>{texto}</p>
        <div style={{ background: bg, border: `1.5px solid ${color}`, borderRadius: 10, padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#111', marginBottom: '1.75rem' }}>
          Estado de tu cuenta: <strong style={{ color }}>{estado}</strong>
        </div>
        {/* 0.14 — Antes esto era un `mailto:` suelto a contacto@. Dos problemas:
            si el navegador no tiene cliente de correo configurado el clic es un
            no-op silencioso —ni navegación, ni error, ni aviso—, y apuntaba a la
            casilla equivocada. Es el peor lugar posible para un botón muerto: le
            pasa a alguien recién registrado, bloqueado, y ese botón es su única
            salida. Ahora la dirección se muestra siempre y se puede copiar,
            aunque el enlace no haga nada. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <a href="mailto:contacto@qapaq.cl" style={{ display: 'block', padding: '0.7rem', borderRadius: 10, background: color, color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: '0.9rem' }}>
            Escribir a soporte
          </a>
          <p style={{ fontSize: '0.8rem', color: '#666', margin: 0, lineHeight: 1.5 }}>
            ¿No se abrió tu correo? Escríbenos a{' '}
            <strong style={{ color: '#111', userSelect: 'all' }}>contacto@qapaq.cl</strong>
          </p>
          <button onClick={cerrarSesion} style={{ padding: '0.7rem', borderRadius: 10, border: '1.5px solid #ddd', background: 'transparent', color: '#666', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  )
}

// Protege rutas por rol — redirige al dashboard si el rol no está permitido
function RutaRol({ children, usuario, roles }) {
  const location = useLocation()
  if (!usuario) return (
    <div style={{ display:'flex', justifyContent:'center', padding:'3rem' }}>
      <div className="spinner" />
    </div>
  )
  if (!roles.includes(usuario.rol)) {
    auditLog({ actorId: usuario.id, actorRol: usuario.rol, accion: 'acceso.denegado', detalle: { ruta: location.pathname, roles_requeridos: roles } })
    return <Navigate to="/app" replace />
  }
  return children
}

export default function App() {
  const [session, setSession]   = useState(null)
  const [usuario, setUsuario]   = useState(undefined) // undefined = cargando, null = no encontrado
  const [cargando, setCargando] = useState(true)
  const [sinConexion, setSinConexion] = useState(false)

  useEffect(() => {
    let resuelta = false
    const limite = setTimeout(() => {
      if (resuelta) return
      setSinConexion(true)
      setCargando(false)
    }, LIMITE_SESION_MS)

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        resuelta = true
        clearTimeout(limite)
        setSession(session)
        setSinConexion(false)
        setCargando(false)
      })
      .catch(() => {
        resuelta = true
        clearTimeout(limite)
        setSinConexion(true)
        setCargando(false)
      })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      if (!session) setUsuario(undefined)
    })

    return () => {
      clearTimeout(limite)
      subscription.unsubscribe()
    }
  }, [])

  // Carga el rol del usuario una vez establecida la sesión
  useEffect(() => {
    if (!session) return
    supabase
      .from('usuario')
      .select('id, rol, estado')
      .eq('auth_id', session.user.id)
      .maybeSingle()
      .then(({ data }) => setUsuario(data ?? null))
  }, [session])

  const ruta = window.location.pathname.replace(/\/+$/, '') || '/'
  const rutaPublica = RUTAS_PUBLICAS.includes(ruta)

  if (sinConexion && !rutaPublica) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', padding: '1.5rem', textAlign: 'center', gap: '1rem' }}>
        <p style={{ fontSize: '1.05rem', fontWeight: 600, margin: 0 }}>No pudimos conectar con QAPAQ.</p>
        <p style={{ color: '#666', margin: 0 }}>Intenta de nuevo en unos minutos.</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>Reintentar</button>
      </div>
    )
  }

  if (cargando && !rutaPublica) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div className="spinner" />
      </div>
    )
  }

  // El bypass se leía sólo de la URL, y las navegaciones internas de React
  // Router no arrastran el query string: bastaba un `navigate('/app')` para
  // caer de vuelta en la pantalla de mantenimiento. Eso lo volvía inservible
  // para recorrer la app. Ahora, una vez presentada la clave, se recuerda por
  // la pestaña — `sessionStorage`, no `localStorage`: al cerrar la pestaña se
  // olvida, que es lo que corresponde a un acceso de preview.
  if (new URLSearchParams(window.location.search).get('v') === PREVIEW_KEY) {
    try { sessionStorage.setItem('qapaq_preview', PREVIEW_KEY) } catch { /* modo privado */ }
  }
  let preview = false
  try { preview = sessionStorage.getItem('qapaq_preview') === PREVIEW_KEY } catch { /* modo privado */ }

  const rutaAbierta = RUTAS_SIEMPRE_ABIERTAS.some(r => window.location.pathname.startsWith(r))
  if (MANTENIMIENTO && !preview && !rutaAbierta) return <PaginaMantenimiento />

  return (
    <BrowserRouter>
      <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><div className="spinner" /></div>}>
      <Routes>
        <Route path="/"             element={<LandingPage />} />
        <Route path="/transportistas" element={<PaginaInfo slug="transportistas" />} />
        <Route path="/empresas"       element={<PaginaInfo slug="empresas" />} />
        <Route path="/como-funciona"  element={<PaginaInfo slug="como-funciona" />} />
        <Route path="/quienes-somos"  element={<PaginaInfo slug="quienes-somos" />} />
        <Route path="/app/login"      element={<Login />} />
        <Route path="/app/registro"   element={<Registro />} />
        <Route path="/app/terminos"       element={<Terminos />} />
        <Route path="/app/privacidad"     element={<Privacidad />} />
        <Route path="/app/pago/resultado" element={<PagoResultado />} />

        {/* Restablecer contraseña (0.20). Va fuera del shell protegido: se
            llega desde el enlace del correo sin haber iniciado sesión, y está
            en RUTAS_SIEMPRE_ABIERTAS para que el modo mantenimiento no la
            tape. Sirve también para cambiarla estando dentro. */}
        <Route path="/app/clave" element={<RestablecerClave />} />

        {/* Verificación pública de un Acuerdo (7.7). Va en la raíz y no bajo
            /app a propósito: la usa un tercero que no tiene cuenta —un juez,
            un seguro, el cliente del oferente—. `verificar_acuerdo` tiene
            GRANT a `anon` justamente para esto. El pie del AcuerdoPanel ya
            promete esta dirección: qapaq.cl/verificar */}
        <Route path="/verificar" element={<VerificarAcuerdo />} />

        <Route path="/app" element={
          <RutaProtegida session={session} usuario={usuario}>
            <Layout session={session} />
          </RutaProtegida>
        }>
          <Route index element={<Dashboard />} />
          <Route path="publicar" element={
            <RutaRol usuario={usuario} roles={['oferente']}>
              <PublicarCarga />
            </RutaRol>
          } />
          <Route path="buscar" element={
            <RutaRol usuario={usuario} roles={['chofer', 'admin_flota']}>
              <BuscarCargas />
            </RutaRol>
          } />
          <Route path="envios"      element={<MisEnvios />} />
          <Route path="envios/:id"  element={<DetalleEnvio />} />
          <Route path="entrega/:token" element={<EntregaQR />} />
          <Route path="remate/:id"  element={<RemateView />} />
          <Route path="desistir/:id" element={<Desistir />} />
          <Route path="perfil"      element={<Perfil />} />
          <Route path="flota" element={
            <RutaRol usuario={usuario} roles={['chofer', 'admin_flota']}>
              <AdminFlota />
            </RutaRol>
          } />
          <Route path="chat"        element={<Chat />} />
          {/* Desde acá abajo es operación de QAPAQ, no de un cliente.
              Antes colgaba de admin_flota, que es el jefe de flota de una
              empresa de transporte: cualquiera que se registrara así veía la
              contabilidad de la plataforma. Ver src/lib/roles.js */}
          <Route path="repositorio" element={
            <RutaRol usuario={usuario} roles={[ROL.SUPERADMIN]}>
              <Repositorio />
            </RutaRol>
          } />
          <Route path="crm" element={
            <RutaRol usuario={usuario} roles={[ROL.SUPERADMIN]}>
              <CRM />
            </RutaRol>
          } />
          <Route path="servicios"   element={<PayPerUse />} />
          <Route path="whatsapp"    element={<WhatsAppConfig />} />
          <Route path="finanzas" element={
            <RutaRol usuario={usuario} roles={[ROL.SUPERADMIN]}>
              <AdminFinanzas />
            </RutaRol>
          } />
          {/* GPS se queda: el jefe de flota ve SUS camiones, filtrado por empresa */}
          <Route path="gps" element={
            <RutaRol usuario={usuario} roles={[ROL.ADMIN_FLOTA]}>
              <ControlGPS />
            </RutaRol>
          } />
          <Route path="mercado" element={
            <RutaRol usuario={usuario} roles={[ROL.SUPERADMIN]}>
              <ReporteMercado />
            </RutaRol>
          } />
          {/* La base comercial es de QAPAQ. Antes la veía cualquier chofer. */}
          <Route path="prospectos" element={
            <RutaRol usuario={usuario} roles={[ROL.SUPERADMIN]}>
              <Prospectos />
            </RutaRol>
          } />
          <Route path="sugerencias" element={<Sugerencias />} />
          <Route path="mis-datos"   element={<MisDatos />} />
          <Route path="privacidad-admin" element={
            <RutaRol usuario={usuario} roles={[ROL.SUPERADMIN]}>
              <AdminPrivacidad />
            </RutaRol>
          } />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
